import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { useTranslation } from '../../../i18n';
import type { Note, NoteType, NotePriority } from '../../../types/api';

// STD-015: server state for the Statistics domain. apiService is imported here
// (a *.queries.ts file) per STD-016. Errors are toasted by the global QueryCache
// handler (lib/queryClient); meta.errorMessage supplies the i18n fallback.

// Filters that narrow a notes query. Used both as the queryFn params and as the
// trailing segment of the query key so each filter combination caches separately.
export interface NotesQueryFilters {
  startDate?: string;
  endDate?: string;
  noteType?: NoteType;
  priority?: NotePriority;
  includeResolved?: boolean;
}

export const statisticsKeys = {
  all: ['statistics'] as const,
  maintenanceTimeline: (droneSerial: string) =>
    [...statisticsKeys.all, 'maintenance-timeline', droneSerial] as const,
  // Notes for a drone. Mutations invalidate the serial-level prefix
  // (`notes(serial)`) which, by TanStack's partial-match semantics, refetches
  // every filter variant for that drone.
  notes: (droneSerial: string, filters?: NotesQueryFilters) =>
    filters
      ? ([...statisticsKeys.all, 'notes', droneSerial, filters] as const)
      : ([...statisticsKeys.all, 'notes', droneSerial] as const),
};

// Notes for a drone — drives the timeline markers and the notes list. The hook
// is disabled when no serial is selected (returns []). Replaces the former
// manual useState/useEffect + window-event refresh in useNotesHistory.
export function useNotesQuery(droneSerial: string | undefined, filters?: NotesQueryFilters) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: statisticsKeys.notes(droneSerial ?? '', filters),
    enabled: !!droneSerial,
    meta: { errorMessage: t('notes.failedToLoad') },
    queryFn: async (): Promise<Note[]> => {
      const fetchParams = {
        limit: 100,
        start_date: filters?.startDate,
        end_date: filters?.endDate,
        note_type: filters?.noteType,
        priority: filters?.priority,
        include_resolved: filters?.includeResolved,
      };
      const cleanParams = Object.fromEntries(
        Object.entries(fetchParams).filter(([, value]) => value !== undefined)
      );
      const notes = await apiService.getNotes(droneSerial as string, cleanParams);
      return Array.isArray(notes) ? notes : [];
    },
  });
}

// Maintenance timeline for a drone — drives the Gantt chart MAINT axis.
export function useMaintenanceTimelineQuery(droneSerial: string) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: statisticsKeys.maintenanceTimeline(droneSerial),
    enabled: !!droneSerial,
    meta: { errorMessage: t('maintenanceTab.failedToLoadTimeline') },
    queryFn: async () => {
      return apiService.getMaintenanceTimeline(droneSerial);
    },
  });
}
